/**
 * A product rental's lifecycle — a phone or SIM handed to a customer under
 * IMEI/ICCID tracking, distinct from a sale: the unit stays owned by the shop
 * and comes back. Shaped like the repair and order machines for the same
 * reason: one pattern for "what can this record legally become next"
 * everywhere it appears.
 *
 * `overdue` is reached only by the nightly sweep (or set back to `active` by
 * staff extending the due date) — it is never a status staff pick by hand on
 * creation.
 */
export const RENTAL_STATUSES = ['active', 'overdue', 'returned', 'cancelled', 'not_returned'];
/** Truly final — nothing follows. */
export const TERMINAL_RENTAL_STATUSES = [
    'returned',
    'cancelled',
    'not_returned',
];
export const RENTAL_TRANSITIONS = {
    active: ['overdue', 'returned', 'cancelled', 'not_returned'],
    // Staff can push the due date out and bring an overdue rental back to
    // active, or close it out as returned or cancelled.
    overdue: ['returned', 'active', 'cancelled', 'not_returned'],
    // Undone only through the dedicated reopen endpoint (t1254), which also
    // puts the units back out on rental.
    returned: [],
    // Like `returned`, reachable only through its own dedicated endpoint —
    // never picked as a generic transition target.
    cancelled: [],
    // The customer never brought the unit back (t1255) — also reachable only
    // through its own endpoint, which charges for it and writes the unit off.
    not_returned: [],
};
export function canTransitionRental(from, to) {
    return RENTAL_TRANSITIONS[from].includes(to);
}
/**
 * How the rental fee repeats. `one_time` covers a flat rental charge;
 * `daily`/`weekly` make the fee a rate per day/week, charged for every
 * day/week between start and due date (t1366, see `rentalFeeTotalCents`);
 * `monthly` rentals are billed by staff recording a new fee payment each
 * period — there is no automatic recurring charge yet (no Stripe
 * subscription or saved-card auto-charge support).
 */
export const RENTAL_BILLING_INTERVALS = ['one_time', 'daily', 'weekly', 'monthly'];
/**
 * The automated notices the daily rental notification job can send (t1082).
 * One row per rental + kind + due date in `rental_notifications` keeps each
 * notice from going out twice.
 */
export const RENTAL_NOTIFICATION_KINDS = [
    'due_reminder',
    'overdue_staff_email',
    'overdue_customer_email',
    'overdue_call',
    'due_reminder_sms',
    'overdue_customer_sms',
];
/**
 * The three scans of a rental return (t1105): the customer announces the
 * drop-off at the kiosk, staff scan it as received, staff scan the item to
 * verify it is the one that was rented. Every scan lands in
 * `rental_return_scans`, matched or not.
 */
export const RENTAL_RETURN_SCAN_STEPS = ['announce', 'receive', 'verify'];
export const RENTAL_RETURN_SCAN_RESULTS = [
    'matched',
    'already_scanned',
    /** The unit is out on a different open rental. */
    'wrong_rental',
    /** A known unit that is not out on any open rental. */
    'not_on_rental',
    'unknown_code',
    /** The rental the code points at is already returned or cancelled. */
    'rental_closed',
];
//# sourceMappingURL=rental-states.js.map